/**
 * NAH.SH Journey Planner URL Builder
 *
 * Builds URLs for the NAH.SH (Nahverkehr Schleswig-Holstein) public transport
 * route planner. Uses the current planer at www.nah.sh (post-migration format).
 *
 * SECURITY: All user-supplied values MUST be encoded with encodeURIComponent
 * to prevent URL injection and XSS via crafted input (e.g. &foo=bar, javascript:).
 *
 * @see https://www.nah.sh/de/fahrplan/planer/
 */

/** Base URL for the NAH.SH journey planner (current format as of 2025) */
export const NAHSH_PLANER_BASE_URL = 'https://www.nah.sh/de/fahrplan/planer/';

/**
 * Safely encodes a location string for use in URL query parameters.
 * Trims whitespace and returns empty string for falsy/whitespace-only input.
 *
 * @param {string} value - Raw location string (address, station name, etc.)
 * @returns {string} Trimmed value, or empty string
 */
function normalizeLocation(value) {
    if (value == null || typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    return trimmed;
}

/**
 * Builds the NAH.SH journey planner URL with optional start and destination.
 *
 * Parameters S (Start) and Z (Ziel/destination) accept plain-text addresses
 * or station names. The planer resolves them on the NAH.SH side.
 *
 * @param {Object} options
 * @param {string} [options.start] - Start location (origin)
 * @param {string} [options.destination] - Destination (Ziel)
 * @param {string} [options.language='de_DE'] - Language code for the planer
 * @returns {string} Full URL ready for window.open or href
 */
export function buildNahShPlanerUrl({ start = '', destination = '', language = 'de_DE' } = {}) {
    const params = new URLSearchParams();

    params.set('language', language);
    params.set('P', 'TP'); // Travel planner

    const startNorm = normalizeLocation(start);
    const destNorm = normalizeLocation(destination);

    if (startNorm) {
        params.set('S', startNorm);
    }
    if (destNorm) {
        params.set('Z', destNorm);
    }

    // Trigger search immediately when both locations are provided
    if (startNorm && destNorm) {
        params.set('start', 'yes');
    }

    const queryString = params.toString();
    const base = NAHSH_PLANER_BASE_URL.replace(/\/?$/, '');

    return queryString ? `${base}?${queryString}` : base;
}
